import React, { useState, useEffect } from 'react';

/**
 * Hero Section Component
 * Shows different content based on authentication state
 * Listens to auth state changes from localStorage
 */
export default function HeroSection({ analytics }) {
  const [authState, setAuthState] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Get auth state from localStorage
    const getAuthState = () => {
      try {
        const stored = localStorage.getItem('pinkWalletAuth');
        return stored ? JSON.parse(stored) : null;
      } catch (error) {
        console.error('Error reading auth state:', error);
        return null;
      }
    };

    // Update state
    const updateAuthState = () => {
      const state = getAuthState();
      setAuthState(state);
      setIsLoggedIn(state !== null);
    };

    // Initial load
    updateAuthState();

    // Listen for auth state changes
    const handleAuthChange = (event) => {
      setAuthState(event.detail);
      setIsLoggedIn(event.detail !== null);
    };

    window.addEventListener('authStateChanged', handleAuthChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, []);

  const handleShowCard = () => {
    // Trigger the membership card modal from base.njk
    const event = new CustomEvent('showMembershipCard');
    window.dispatchEvent(event);
    analytics.trackInteraction('show_membership_card');
  };

  const firstName = authState?.name?.split(' ')[0] || 'Member';

  return (
    <section
      className="py-8 md:py-20 relative overflow-hidden"
      style={{ backgroundColor: '#2e7d32' }}
    >
      <div
        className="absolute overflow-visible"
        style={{
          width: '300%',
          height: '300%',
          left: '-100%',
          top: '-100%',
          backgroundImage: 'url(/assets/logo.svg)',
          backgroundSize: '400px',
          backgroundRepeat: 'repeat',
          filter: 'grayscale(100%) brightness(0.85) sepia(90%) hue-rotate(90deg) saturate(10%)',
          transform: 'rotate(-15deg)',
          opacity: 0.25,
        }}
      ></div>

      {/* Logged Out Hero */}
      {!isLoggedIn && (
        <div className="container-custom relative">
          <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-white">
            Keep your money local, keep your money queer.
          </h1>
          <p className="text-lg md:text-xl text-white">
            Discover LGBTQ+ events and businesses in West Michigan. Support the community and save with a Pink
            Wallet membership.
          </p>
          <div className="flex justify-center md:justify-start gap-4 md:gap-6 mt-4">
            <a href="/subscribe/" className="btn btn-primary no-hover">
              Join to Support & Save
            </a>
            <a href="/login/" className="btn btn-secondary no-hover">
              Log In
            </a>
          </div>
        </div>
      )}

      {/* Logged In Hero */}
      {isLoggedIn && (
        <div className="container-custom relative">
          <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-white">
            Welcome back, {firstName}!
          </h1>

          {/* Trial User View - Shows both Card and Upgrade options */}
          {authState?.isTrial ? (
            <>
              <p className="text-lg md:text-xl text-white mb-6">
                You're currently on a <strong>Free Trial</strong>. Enjoy this virtual card and upgrade to keep it!
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6">
                <button onClick={handleShowCard} className="btn btn-primary no-hover">
                  <svg
                    className="w-5 h-5 inline-block mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    ></path>
                  </svg>
                  Show My Card
                </button>
                <a href="/subscribe/select-tier/" className="btn btn-secondary no-hover bg-brand-600 text-white border-brand-600 hover:bg-brand-700">
                  Upgrade Membership
                </a>
                <a href="/profile/" className="btn btn-secondary no-hover">
                  My Profile
                </a>
              </div>
            </>
          ) : authState?.tier === 'free' ? (
            <>
              <p className="text-lg md:text-xl text-white mb-6">
                You're currently on the <strong>Free</strong> plan. Upgrade to unlock exclusive discounts!
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6">
                <a href="/subscribe/select-tier/" className="btn btn-secondary no-hover bg-brand-600 text-white border-brand-600 hover:bg-brand-700">
                  Upgrade Membership
                </a>
                <a href="/profile/" className="btn btn-secondary no-hover">
                  My Profile
                </a>
              </div>
            </>
          ) : (
            <>
              <p className="text-lg md:text-xl text-white mb-6">
                Thank you for being a Pink Wallet member!
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6">
                <button onClick={handleShowCard} className="btn btn-primary no-hover">
                  <svg
                    className="w-5 h-5 inline-block mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    ></path>
                  </svg>
                  Show My Card
                </button>
                <a href="/profile/" className="btn btn-secondary no-hover">
                  <svg
                    className="w-5 h-5 inline-block mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                  </svg>
                  My Profile
                </a>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
